import { type ReactNode } from 'react';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  User,
  Palette,
  Bell,
  Building2,
  Globe,
  Network,
  Users,
  Shield,
  FileSearch,
  Cog,
  CreditCard,
  Server,
  Flag,
  Building,
  KeyRound,
  PackageCheck,
  Activity,
  Radio,
  type LucideIcon,
} from 'lucide-react';

interface SettingsNavItem {
  title: string;
  href: string;
  icon: LucideIcon;
}

interface SettingsNavCategory {
  label: string;
  items: SettingsNavItem[];
}

function useSettingsNavigation(): SettingsNavCategory[] {
  const { hasPermission, hasAnyPermission, isSuperAdmin } = usePermissions();

  const categories: SettingsNavCategory[] = [];
  const canReadDepartments = hasPermission('hrms:department:read:tenant');
  const canReadTeams = hasPermission('hrms:team:read:tenant');
  const canReadTenantSettings =
    hasPermission('tenant:access:read:tenant') || hasPermission('tenant:settings:update:tenant');
  const canReadUsers = hasPermission('tenant:users:read:tenant');
  const canReadRoles = hasPermission('abac:role:read:tenant');
  const canReadAuthConfig = hasPermission('auth:config:read:tenant');
  const canReadAuditLogs = hasAnyPermission(['auth:audit:read:tenant', 'hrms:audit:read:tenant']);
  const canUseSecuritySettings = hasAnyPermission([
    'tenant:users:read:tenant',
    'abac:role:read:tenant',
    'auth:config:read:tenant',
    'auth:audit:read:tenant',
    'hrms:audit:read:tenant',
  ]);
  const canReadBilling = hasPermission('tenant:plans:read:tenant');
  const canReadEventOperations = hasPermission('admin:operations:events:read:global');

  // My Account — always visible
  categories.push({
    label: 'My Account',
    items: [
      { title: 'Profile', href: '/settings/profile', icon: User },
      { title: 'Preferences', href: '/settings/preferences', icon: Palette },
      { title: 'Notifications', href: '/settings/notifications', icon: Bell },
    ],
  });

  // Organization - show only the organization tabs the user can read
  if (canReadDepartments || canReadTeams || canReadTenantSettings) {
    const organizationItems: SettingsNavItem[] = [];

    if (canReadDepartments) {
      organizationItems.push({
        title: 'Company Profile',
        href: '/settings/organization/profile',
        icon: Building2,
      });
      organizationItems.push({
        title: 'Departments',
        href: '/settings/organization/departments',
        icon: Network,
      });
    }

    if (canReadTenantSettings) {
      organizationItems.push({
        title: 'Regional Settings',
        href: '/settings/organization/regional',
        icon: Globe,
      });
    }

    if (canReadTeams) {
      organizationItems.push({ title: 'Teams', href: '/settings/organization/teams', icon: Users });
    }

    categories.push({
      label: 'Organization',
      items: organizationItems,
    });
  }

  // Users & Security
  if (canUseSecuritySettings) {
    const securityItems: SettingsNavItem[] = [];

    if (canReadUsers) {
      securityItems.push({ title: 'Users', href: '/settings/users', icon: Users });
    }

    if (canReadRoles) {
      securityItems.push({ title: 'Roles & Permissions', href: '/settings/roles', icon: Shield });
    }

    if (canReadAuthConfig) {
      securityItems.push({ title: 'Authentication', href: '/settings/auth', icon: Cog });
    }

    if (canReadAuditLogs) {
      securityItems.push({ title: 'Audit Logs', href: '/settings/audit-logs', icon: FileSearch });
    }

    categories.push({
      label: 'Users & Security',
      items: securityItems,
    });
  }

  // Billing
  if (canReadBilling) {
    categories.push({
      label: 'Billing',
      items: [{ title: 'Plan & Usage', href: '/settings/billing', icon: CreditCard }],
    });
  }

  // System — superadmin only
  if (isSuperAdmin) {
    categories.push({
      label: 'System',
      items: [
        { title: 'Platform Config', href: '/settings/system/config', icon: Server },
        { title: 'Operations', href: '/settings/system/operations', icon: Activity },
        { title: 'Feature Flags', href: '/settings/system/feature-flags', icon: Flag },
        { title: 'Plans', href: '/settings/system/plans', icon: PackageCheck },
        { title: 'Tenants', href: '/settings/system/tenants', icon: Building },
        { title: 'Zepto Platform', href: '/settings/system/zepto-platform', icon: KeyRound },
        ...(canReadEventOperations
          ? [{ title: 'Event Governance', href: '/settings/system/events', icon: Radio }]
          : []),
      ],
    });
  }

  return categories;
}

interface SettingsLayoutProps {
  children: ReactNode;
}

export function SettingsLayout({ children }: SettingsLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const categories = useSettingsNavigation();

  // Check if a pathname matches an item's href (exact match or starts with href + "/")
  const isItemMatch = (pathname: string, href: string) =>
    pathname === href || pathname.startsWith(href + '/');

  // Find active category by matching current URL to any item href
  const activeCategory =
    categories.find((cat) => cat.items.some((item) => isItemMatch(location.pathname, item.href))) ||
    categories[0];

  // Find active sub-tab within the active category
  const activeItemHref =
    activeCategory?.items.find((item) => isItemMatch(location.pathname, item.href))?.href || '';

  const handleSubTabChange = (href: string) => {
    void navigate({ to: href });
  };

  return (
    <div className="flex-1 space-y-6">
      <div>
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">
          {activeCategory?.label || 'Settings'}
        </h2>
        <p className="text-sm text-muted-foreground">
          Manage your account, organization, and application configuration
        </p>
      </div>

      {/* Sub-tabs for the active category */}
      {activeCategory && activeCategory.items.length > 1 && (
        <Tabs value={activeItemHref} onValueChange={handleSubTabChange}>
          <TabsList className="h-auto max-w-full flex-wrap justify-start">
            {activeCategory.items.map((item) => (
              <TabsTrigger
                key={item.href}
                value={item.href}
                className="min-h-9 whitespace-normal text-center"
              >
                {item.title}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      )}

      {/* Page content from router */}
      <div>{children}</div>
    </div>
  );
}
